import { StarIcon } from '@heroicons/react/20/solid';
import { useTranslation } from 'react-i18next';
import { ReviewsStatistics as ReviewsStatisticsType } from '../../../types/retail-product';

interface ReviewsStatisticsProps {
  statistics: ReviewsStatisticsType;
  setIsReviewModalOpen: (value: boolean) => void;
}

export default function ReviewsStatistics({ statistics, setIsReviewModalOpen }: ReviewsStatisticsProps) {
  const { t } = useTranslation();

  const reviewsCount = statistics?.reviews_count || 0;
  const ratingDistribution = statistics.rating_distribution || [];

  return (
    <div className='order-1 lg:order-1 pb-5 lg:pb-0'>
      <div className='flex items-center justify-between'>
        <h2 className='text-2xl font-bold tracking-tight text-black'>
          {t('productDetails.reviews.productReviews')}
        </h2>
        {reviewsCount > 0 && (
          <p className='text-sm font-bold text-secondary tracking-tight mt-1'>
            {t('productDetails.reviews.ReviewsAveragebasedOnCount', {
              count: reviewsCount,
              average: statistics.reviews_average,
            })}
          </p>
        )}
      </div>

      <div className='mt-6' dir='ltr'>
        <h3 className='sr-only'>Review data</h3>
        {reviewsCount > 0 ? (
          <dl className='space-y-3'>
            {ratingDistribution.map((item) => (
              <div key={item.rating} className='flex items-center text-sm'>
                <dt className='flex flex-1 items-center'>
                  <p className='w-3 font-medium text-secondary'>
                    {item.rating}
                    <span className='sr-only'> star reviews</span>
                  </p>
                  <div aria-hidden='true' className='ml-1 flex flex-1 items-center rtl:space-x-reverse'>
                    <StarIcon
                      aria-hidden='true'
                      className='h-5 w-5 flex-shrink-0 text-primary'
                    />
                    <div className='relative ml-3 flex-1'>
                      <div className='h-3 rounded-full border border-gray-200 bg-gray-100' />
                      <div
                        style={{ width: `${item.percentage}%` }}
                        className='absolute inset-y-0 rounded-full border border-primary bg-primary'
                      />
                    </div>
                  </div>
                </dt>
                <dd className='ml-3 w-10 text-right text-sm tabular-nums text-secondary'>
                  {Math.round(item.percentage)}
                  %
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className='flex pt-2 items-center justify-center rounded-lg'>
            <p className='text-md font-semibold text-secondary'>
              {t('productDetails.reviews.noReviewStatistics')}
            </p>
          </div>
        )}
      </div>

      <div className='mt-10'>
        <h3 className='text-lg font-medium text-black'>{t('productDetails.reviews.addReview')}</h3>
        <p className='mt-1 text-sm text-secondary'>
          {t('productDetails.reviews.reviewPrompt')}
        </p>
        <button
          onClick={() => setIsReviewModalOpen(true)}
          type='button'
          className='mt-6 inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary px-8 py-3 h-11 text-base font-medium text-white shadow-sm hover:bg-lime-600 disabled:bg-lime-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600 sm:w-full'
        >
          {t('productDetails.reviews.addReviewBtn')}
        </button>
      </div>
    </div>
  );
}
